import { useEffect, useRef, useState } from "react";
import { AnimatePresence } from "framer-motion";
import "@/App.css";
import {
  BrowserRouter,
  Routes,
  Route,
  useLocation,
  useNavigate,
  Navigate,
} from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { LangProvider } from "@/i18n";
import api from "@/lib/api";
import { playCrystal } from "@/lib/sound";
import { Toaster } from "@/components/ui/sonner";
import { Layout } from "@/components/Layout";
import Login from "@/pages/Login";
import MapHome from "@/pages/MapHome";
import Chat from "@/pages/Chat";
import Creatures from "@/pages/Creatures";
import Profile from "@/pages/Profile";
import { Splash } from "@/components/Splash";
import { Sparkles } from "lucide-react";

const LoadingScreen = () => (
  <div className="flex min-h-screen items-center justify-center bg-[#050814]">
    <Sparkles className="h-10 w-10 animate-pulse text-[#D4AF37]" />
  </div>
);

const AuthCallback = () => {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const hash = window.location.hash;
    const sessionId = new URLSearchParams(hash.replace("#", "")).get("session_id");
    if (!sessionId) {
      navigate("/");
      return;
    }
    (async () => {
      try {
        const linkGuestToken = localStorage.getItem("link_guest_token");
        const res = await api.post("/auth/session", {
          session_id: sessionId,
          link_guest_token: linkGuestToken || null,
        });
        localStorage.removeItem("link_guest_token");
        if (res.data.session_token) {
          localStorage.setItem("session_token", res.data.session_token);
        }
        if (res.data.linked && res.data.stats) {
          sessionStorage.setItem("bind_welcome", JSON.stringify(res.data.stats));
        }
        setUser(res.data.user);
        window.history.replaceState(null, "", "/chat");
        navigate("/chat", { replace: true });
      } catch (e) {
        navigate("/", { replace: true });
      }
    })();
  }, [navigate, setUser]);

  return <LoadingScreen />;
};

const Protected = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/" replace />;
  return <Layout>{children}</Layout>;
};

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Login />} />
      <Route path="/chat" element={<Protected><Chat /></Protected>} />
      <Route path="/map" element={<Protected><MapHome /></Protected>} />
      <Route path="/creatures" element={<Protected><Creatures /></Protected>} />
      <Route path="/profile" element={<Protected><Profile /></Protected>} />
      <Route path="*" element={<Navigate to="/chat" replace />} />
    </Routes>
  );
}

function App() {
  const [showSplash, setShowSplash] = useState(() => !sessionStorage.getItem("splash_seen"));

  useEffect(() => {
    if (!showSplash) return;
    const timer = setTimeout(() => {
      sessionStorage.setItem("splash_seen", "1");
      setShowSplash(false);
    }, 3000);
    return () => clearTimeout(timer);
  }, [showSplash]);

  useEffect(() => {
    const onClick = (e) => {
      if (e.target.closest('button, a[href], .crystal, [role="button"]')) {
        playCrystal();
      }
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);

  return (
    <div className="App">
      <AnimatePresence>{showSplash && <Splash key="splash" />}</AnimatePresence>
      <BrowserRouter>
        <LangProvider>
          <AuthProvider>
            <AppRouter />
            <Toaster position="top-center" theme="dark" />
          </AuthProvider>
        </LangProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
